<template>
  <v-dialog fullscreen v-model="showDialog">
    <v-card>
      <v-card-title class="secondary white--text rounded-0">
        <span>Bulk Change Customer</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="resetForm(); showDialog = false">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-3 pb-3">
        <v-row align="center">
          <v-col cols="12" sm="8" md="6" class="Form-Columns">
            <v-text-field
              outlined
              dense
              label="DA No (comma separated)"
              hide-details="auto"
              placeholder="e.g. DA123, DA456, DA789"
              v-model="daNos"
              @keyup.enter="searchList"
            />
          </v-col>
          <v-col cols="auto" class="Form-Columns d-flex align-center">
            <v-btn depressed color="primary" class="mr-2" @click="searchList">
              Search
            </v-btn>
            <v-btn depressed color="secondary" @click="resetForm">
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="mt-2" />

      <v-card-text class="py-3">
        <v-row align="center">
          <v-col cols="auto">
            <span class="text-subtitle-1">
              Total Selected: <strong>{{ selectedRows.length }}</strong>
            </span>
          </v-col>
          <v-col cols="12" sm="5" md="3" class="Form-Columns">
            <Autocomplete
              label="Select customer to apply"
              :items="customerList"
              itemText="company_title"
              itemValue="id"
              :loading="customerLoading"
              :totalItems="customerCount"
              @updateList="fetchCustomers"
              hide-details="auto"
              dense
              v-model="bulkCustomer"
              @input="applyToSelected"
            />
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <v-btn
              depressed
              color="primary"
              :disabled="!eligibleRowCount"
              :loading="submitting"
              @click="confirmDialog = true"
            >
              Apply Customer Change ({{ eligibleRowCount }})
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="rowErrors.length" class="mt-2" dense>
          <v-col cols="12">
            <div class="error-strip">
              <div class="error-strip__header">
                <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                <span class="caption font-weight-bold error--text">{{ rowErrors.length }} row(s) with issues</span>
              </div>
              <div class="error-strip__list">
                <div v-for="(err, i) in rowErrors" :key="i" class="error-strip__row">
                  <span class="font-weight-bold error--text mr-2">{{ err.da_no }}</span>
                  <span class="caption">{{ err.message }}</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-text class="pt-2 pb-0">
        <AgGridVue
          style="width: 100%; height: calc(100vh - 260px)"
          class="ag-theme-alpine"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          rowSelection="multiple"
          @selection-changed="setSelectedRows"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :rowData="allRowList"
        />
        <v-row v-if="totalItems > 0" class="ma-0">
          <v-col cols="12" class="d-flex justify-end pa-0 pt-1">
            <Pagination
              :pageNo="pageNo"
              :totalItems="totalItems"
              :pageSize="itemsPerPage"
              @itemsPerPageChange="itemsPerPageChanged"
              @prevPage="prevPage"
              @nextPage="nextPage"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6">Confirm Bulk Customer Change</v-card-title>
        <v-card-text>
          Apply the selected new customer to <strong>{{ eligibleRowCount }}</strong> DA(s)?
          This will also update rate (and tax) on each DA and its legs.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn depressed @click="confirmDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" @click="submitForm">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showResultsDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h6">
          Bulk Change Results
        </v-card-title>
        <v-card-text style="max-height: 440px; overflow-y: auto">
          <div class="mb-3">
            <v-chip small label color="success" text-color="white" class="mr-2">{{ succeededCount }} succeeded</v-chip>
            <v-chip small label color="error" text-color="white" v-if="failedCount">{{ failedCount }} failed</v-chip>
          </div>
          <v-alert
            v-for="(row, i) in results"
            :key="i"
            :type="row.success ? 'success' : 'error'"
            outlined
            dense
            class="mb-2"
          >
            <div class="font-weight-bold">{{ row.da_no || row.da_id }}</div>
            <div v-if="row.success" class="caption">
              → {{ row.customer_name }}, rate {{ row.rate }}
            </div>
            <div v-else class="caption">{{ row.error }}</div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn depressed color="primary" @click="showResultsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import Pagination from "@/components/General/Pagination.vue";

export default {
  components: { AgGridVue, Autocomplete, Pagination },

  props: {
    value: { type: Boolean, default: false },
  },

  watch: {
    value(val) {
      if (val) {
        this.fetchCustomers({ limit: 10, offset: 0 });
      } else {
        this.resetForm();
      }
    },
  },

  computed: {
    showDialog: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },
    context() { return { parentComponent: this }; },
    eligibleRowCount() {
      return this.allRowList.filter((row) => row._newCustomer).length;
    },
    succeededCount() {
      return this.results.filter((r) => r.success).length;
    },
    failedCount() {
      return this.results.filter((r) => !r.success).length;
    },
  },

  data() {
    return {
      daNos: "",
      allRowList: [],
      selectedRows: [],
      bulkCustomer: null,
      customerList: [],
      customerCount: 0,
      customerLoading: false,
      rowErrors: [],
      confirmDialog: false,
      showResultsDialog: false,
      results: [],
      submitting: false,
      pageNo: 1,
      itemsPerPage: 50,
      totalItems: 0,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.resizeGrid(),
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        rowHeight: 40,
      },
      headers: [
        { headerName: "", checkboxSelection: true, headerCheckboxSelection: true, minWidth: 50, maxWidth: 50 },
        { headerName: "DA No.", field: "da_no", minWidth: 120 },
        { headerName: "Status", field: "status", minWidth: 100 },
        { headerName: "Current Customer", field: "customer_name", minWidth: 200 },
        { headerName: "Lane", field: "lane_title", minWidth: 160 },
        { headerName: "Current Rate", field: "rate", minWidth: 110 },
      ],
    };
  },

  methods: {
    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => this.gridOptions.api.sizeColumnsToFit(), 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.resizeGrid();
    },
    setSelectedRows() {
      this.selectedRows = this.gridApi ? this.gridApi.getSelectedRows() : [];
    },
    resetForm() {
      this.daNos = "";
      this.allRowList = [];
      this.selectedRows = [];
      this.bulkCustomer = null;
      this.rowErrors = [];
      this.results = [];
      this.pageNo = 1;
      this.totalItems = 0;
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.searchList();
    },
    prevPage() {
      this.pageNo--;
      this.searchList();
    },
    nextPage() {
      this.pageNo++;
      this.searchList();
    },
    searchList() {
      const normalized = (this.daNos || "")
        .replace(/[\r\n]+/g, ",")
        .replace(/\s*,\s*/g, ",")
        .replace(/,+/g, ",")
        .replace(/^,|,$/g, "");
      if (!normalized) {
        this.allRowList = [];
        this.totalItems = 0;
        return;
      }
      this.$bus.$emit("showLoader", true);
      this.$api.deliveryAdvice
        .getDeliveryAdviceList({
          da_nos: normalized,
          limit: this.itemsPerPage,
          offset: this.itemsPerPage * (this.pageNo - 1),
        })
        .then((res) => {
          this.allRowList = (res.data || []).map((row) => ({ ...row, _newCustomer: null }));
          this.totalItems = res.count || this.allRowList.length;
          this.selectedRows = [];
          this.rowErrors = [];
          this.bulkCustomer = null;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    fetchCustomers(params = {}) {
      this.customerLoading = true;
      const query = {
        company_type: "Customer",
        is_active: true,
        payment_method: "Credit",
        limit: 10,
        offset: 0,
        ...params,
      };
      this.$api.company
        .getCompanySelectList(query)
        .then((res) => {
          this.customerList = params.offset ? [...this.customerList, ...res.data] : res.data;
          this.customerCount = res.count;
        })
        .finally(() => {
          this.customerLoading = false;
        });
    },
    setRowCustomer(row, customer) {
      const target = this.allRowList.find((r) => r.id === row.id);
      if (target) {
        target._newCustomer = customer;
      }
      if (customer) {
        this.rowErrors = this.rowErrors.filter((e) => e.da_id !== row.id);
      }
    },
    setRowError(row, message) {
      this.rowErrors = this.rowErrors.filter((e) => e.da_id !== row.id);
      this.rowErrors.push({ da_id: row.id, da_no: row.da_no, message });
    },
    applyToSelected(value) {
      if (!value || !this.selectedRows.length) return;
      this.selectedRows.forEach((row) => {
        this.setRowCustomer(row, value);
      });
    },
    submitForm() {
      this.confirmDialog = false;
      const rowsToSubmit = this.allRowList.filter((row) => row._newCustomer);
      if (!rowsToSubmit.length) return;

      this.submitting = true;
      this.$bus.$emit("showLoader", true);

      Promise.all(
        rowsToSubmit.map((row) => {
          const customerId = typeof row._newCustomer === "object" ? row._newCustomer.id : row._newCustomer;
          return this.$api.deliveryAdvice
            .previewCustomerRate(row.id, customerId)
            .then(() => ({ row, customerId, ok: true }))
            .catch((err) => ({
              row, customerId, ok: false, error: err?.data?.error || "No rate found.",
            }));
        })
      ).then((checked) => {
        const failed = checked.filter((c) => !c.ok);
        failed.forEach((c) => {
          this.setRowCustomer(c.row, null);
          this.setRowError(c.row, c.error);
        });

        const changes = checked
          .filter((c) => c.ok)
          .map((c) => ({ da_id: c.row.id, customer_id: c.customerId }));

        if (!changes.length) {
          this.submitting = false;
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "No rows have a valid contract for the selected customer.",
            color: "error",
          });
          return;
        }

        this.$api.deliveryAdvice
          .bulkChangeCustomer({ changes })
          .then((res) => {
            this.results = res.data.results || [];
            this.showResultsDialog = true;
            this.$bus.$emit("showLoader", false);
            this.submitting = false;
            this.daNos = "";
            this.allRowList = [];
            this.selectedRows = [];
            this.totalItems = 0;
            this.$emit("refreshList");
          })
          .catch((err) => {
            console.error(err);
            this.$bus.$emit("showLoader", false);
            this.submitting = false;
            this.$bus.$emit("showToastMessage", {
              message: "Failed to apply bulk customer change!",
              color: "error",
            });
          });
      });
    },
  },
};
</script>

<style scoped>
.error-strip {
  border: 1px solid rgba(255, 82, 82, 0.4);
  background: rgba(255, 82, 82, 0.05);
  border-radius: 4px;
}
.error-strip__header {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(255, 82, 82, 0.25);
}
.error-strip__list {
  max-height: 110px;
  overflow-y: auto;
}
.error-strip__row {
  padding: 4px 12px;
  border-bottom: 1px solid rgba(255, 82, 82, 0.12);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.error-strip__row:last-child {
  border-bottom: none;
}
</style>
